import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

import OktaLogin from './components/OktaLogin';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardLayout from './components/DashboardLayout';

import { ThemeProvider } from './contexts/ThemeContext';
import MuiThemeWrapper from './components/ThemeProvider';

import oktaAuth from './config/oktaConfig';
import oktaService from './services/oktaService';
import logger from './utils/logger';

const ChatInterfaceAgent = lazy(() => import('./components/ChatInterfaceAgent'));
const OktaCallback = lazy(() => import('./components/OktaCallback'));
const DashboardPage = lazy(() => import('./components/DashboardPage'));
const UtilizationPage = lazy(() => import('./components/UtilizationPage'));
const TotalCostPage = lazy(() => import('./components/TotalCostPage'));
const HighCostMembersPage = lazy(() => import('./components/HighCostMembersPage'));
const ERCostPage = lazy(() => import('./components/ERCostPage'));
const WatchlistPage = lazy(() => import('./components/WatchlistPage'));
// Phase 2
const RxManagementPage = lazy(() => import('./components/RxManagementPage'));
const HighCautionRxPage = lazy(() => import('./components/HighCautionRxPage'));
const RiskMatrixPage = lazy(() => import('./components/RiskMatrixPage'));
const ProbabilityProfilePage = lazy(() => import('./components/ProbabilityProfilePage'));
const AgeGenderPage = lazy(() => import('./components/AgeGenderPage'));
const DiseasePrevalencePage = lazy(() => import('./components/DiseasePrevalencePage'));
const HighCostGroupsPage = lazy(() => import('./components/HighCostGroupsPage'));
const ProviderRiskPage = lazy(() => import('./components/ProviderRiskPage'));
const UtilManagementPage = lazy(() => import('./components/UtilManagementPage'));
const CaseManagementPage = lazy(() => import('./components/CaseManagementPage'));
// Phase 3
const BrandedGenericPage    = lazy(() => import('./components/BrandedGenericPage'));
const BestBuyGenericPage    = lazy(() => import('./components/BestBuyGenericPage'));
const GenericEquivalentPage = lazy(() => import('./components/GenericEquivalentPage'));
const TherapeuticAltPage    = lazy(() => import('./components/TherapeuticAltPage'));
const PaymentAccuracyPage   = lazy(() => import('./components/PaymentAccuracyPage'));
const AdministeredDrugsPage = lazy(() => import('./components/AdministeredDrugsPage'));
const DMEPage               = lazy(() => import('./components/DMEPage'));
const ClinicalLabPage       = lazy(() => import('./components/ClinicalLabPage'));
const ClaimsProfilePage     = lazy(() => import('./components/ClaimsProfilePage'));
const SpecialCasesPage      = lazy(() => import('./components/SpecialCasesPage'));
const SDOHPage              = lazy(() => import('./components/SDOHPage'));
const PlanManagerPage       = lazy(() => import('./components/PlanManagerPage'));
const PremiumsPage          = lazy(() => import('./components/PremiumsPage'));
const StopLossPage          = lazy(() => import('./components/StopLossPage'));
// Phase 4
const MemberProfilePage         = lazy(() => import('./components/MemberProfilePage'));
// MedPrice Insight
const PricingTransparencyPage   = lazy(() => import('./components/PricingTransparencyPage'));

const LoadingFallback = () => (
  <div className="App">
    <div className="loading-container">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const [authState, setAuthState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to Okta auth state changes
  useEffect(() => {
    // Initialize Okta service
    oktaService.initialize(oktaAuth);

    // Get initial auth state
    const updateAuthState = async () => {
      try {
        logger.debug('[AUTH] Checking authentication status...');

        // Check if we're on the callback route - don't interfere but allow rendering
        if (window.location.pathname === '/login/callback') {
          logger.debug('[AUTH] On callback route, skipping auth check');
          setAuthState({ isAuthenticated: false, isPending: false, error: null });
          setIsLoading(false);
          return;
        }

        const isAuthenticated = await oktaAuth.isAuthenticated();
        logger.debug('[AUTH] isAuthenticated:', isAuthenticated);

        if (isAuthenticated) {
          const idToken = await oktaAuth.tokenManager.get('idToken');
          const accessToken = await oktaAuth.tokenManager.get('accessToken');

          logger.debug('[AUTH] Tokens found:', { idToken: !!idToken, accessToken: !!accessToken });

          const newAuthState = {
            isAuthenticated: true,
            idToken,
            accessToken,
            isPending: false,
            error: null
          };

          setAuthState(newAuthState);
        } else {
          logger.debug('[AUTH] Not authenticated');
          setAuthState({ isAuthenticated: false, isPending: false, error: null });
        }

        setIsLoading(false);
      } catch (error) {
        logger.error('[AUTH] Error getting auth state:', error);
        setAuthState({ isAuthenticated: false, isPending: false, error });
        setIsLoading(false);
      }
    };

    // Handler for auth state changes
    const handleAuthStateChange = (newAuthState) => {
      logger.debug('[AUTH] Auth state changed:', newAuthState?.isAuthenticated);
      if (newAuthState) {
        setAuthState({
          isAuthenticated: newAuthState.isAuthenticated,
          idToken: newAuthState.idToken,
          accessToken: newAuthState.accessToken,
          isPending: false,
          error: newAuthState.error || null
        });
        setIsLoading(false);
      }
    };

    // Subscribe to auth state changes
    oktaAuth.authStateManager.subscribe(handleAuthStateChange);

    // Get initial state
    updateAuthState();

    return () => {
      // Unsubscribe using the handler reference
      oktaAuth.authStateManager.unsubscribe(handleAuthStateChange);
    };
  }, []);

  // Okta login handler
  const handleOktaLogin = async () => {
    try {
      logger.debug('[AUTH] Redirecting to Okta login...');
      await oktaAuth.signInWithRedirect();
    } catch (error) {
      logger.error('[AUTH] Login error:', error);
    }
  };

  // Okta logout handler
  const handleOktaLogout = async () => {
    try {
      logger.debug('[AUTH] Logging out...');
      await oktaAuth.signOut();
    } catch (error) {
      logger.error('[AUTH] Logout error:', error);
    }
  };

  // Get user info from Okta
  const getOktaUser = () => {
    if (authState?.idToken?.claims) {
      return {
        email: authState.idToken.claims.email,
        name: authState.idToken.claims.name,
        sub: authState.idToken.claims.sub
      };
    }
    return null;
  };

  // Safety check for authState - wait for it to initialize
  if (!authState) {
    logger.debug('[AUTH] AuthState is null - waiting for initialization...');
    return (
      <div className="App">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Initializing authentication...</p>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            If this takes too long, check browser console for errors.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: '15px', padding: '8px 16px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check for authentication errors
  if (authState.error) {
    logger.error('[AUTH] Authentication error:', authState.error);
    return (
      <div className="App">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Authentication error: {authState.error.message}</p>
        </div>
      </div>
    );
  }

  if (authState.isPending || isLoading) {
    logger.debug('[AUTH] Auth is pending - showing loading');
    return <LoadingFallback />;
  }

  const isOktaAuthenticated = authState.isAuthenticated;
  const oktaUser = getOktaUser();

  return (
    <div className="App">
      <Suspense fallback={<LoadingFallback />}>
        <ErrorBoundary>
          <Routes>
            {/* Okta OAuth callback route */}
            <Route path="/login/callback" element={<OktaCallback />} />

            {/* Root → redirect to dashboard */}
            <Route path="/" element={
              !isOktaAuthenticated ? (
                <OktaLogin onLogin={handleOktaLogin} />
              ) : (
                <DashboardLayout><DashboardPage user={oktaUser} onLogout={handleOktaLogout} /></DashboardLayout>
              )
            } />

            {/* Agent — chat interface (was /) */}
            <Route path="/agent" element={
              !isOktaAuthenticated ? (
                <OktaLogin onLogin={handleOktaLogin} />
              ) : (
                <ChatInterfaceAgent user={oktaUser} onLogout={handleOktaLogout} />
              )
            } />

            {/* Dashboard route - claims dashboard */}
            <Route path="/dashboard" element={
              !isOktaAuthenticated ? (
                <OktaLogin onLogin={handleOktaLogin} />
              ) : (
                <DashboardLayout><DashboardPage user={oktaUser} onLogout={handleOktaLogout} /></DashboardLayout>
              )
            } />

            {/* Utilization Cost Summary dashboard */}
            <Route path="/utilization" element={
              !isOktaAuthenticated ? (
                <OktaLogin onLogin={handleOktaLogin} />
              ) : (
                <DashboardLayout><UtilizationPage user={oktaUser} onLogout={handleOktaLogout} /></DashboardLayout>
              )
            } />

            {/* Phase 1 dashboards */}
            <Route path="/total-cost" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <TotalCostPage />
            } />
            <Route path="/high-cost-members" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <HighCostMembersPage />
            } />
            <Route path="/er-cost" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <ERCostPage />
            } />
            <Route path="/watchlist" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <WatchlistPage />
            } />
            {/* Phase 2 dashboards */}
            <Route path="/rx-management" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <RxManagementPage />
            } />
            <Route path="/high-caution-rx" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <HighCautionRxPage />
            } />
            <Route path="/risk-matrix" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <RiskMatrixPage />
            } />
            <Route path="/probability" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <ProbabilityProfilePage />
            } />
            <Route path="/age-gender" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <AgeGenderPage />
            } />
            <Route path="/disease-prevalence" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <DiseasePrevalencePage />
            } />
            <Route path="/high-cost-groups" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <HighCostGroupsPage />
            } />
            <Route path="/provider-risk" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <ProviderRiskPage />
            } />
            <Route path="/util-management" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <UtilManagementPage />
            } />
            <Route path="/case-management" element={
              !isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <CaseManagementPage />
            } />
            {/* Phase 3 */}
            <Route path="/branded-generic"    element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <BrandedGenericPage />} />
            <Route path="/best-buy-generic"   element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <BestBuyGenericPage />} />
            <Route path="/generic-equivalent" element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <GenericEquivalentPage />} />
            <Route path="/therapeutic-alt"    element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <TherapeuticAltPage />} />
            <Route path="/payment-accuracy"   element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <PaymentAccuracyPage />} />
            <Route path="/administered-drugs" element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <AdministeredDrugsPage />} />
            <Route path="/dme"                element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <DMEPage />} />
            <Route path="/clinical-lab"       element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <ClinicalLabPage />} />
            <Route path="/claims-profile"     element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <ClaimsProfilePage />} />
            <Route path="/special-cases"      element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <SpecialCasesPage />} />
            <Route path="/sdoh"               element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <SDOHPage />} />
            <Route path="/plan-manager"       element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <PlanManagerPage />} />
            <Route path="/premiums"           element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <PremiumsPage />} />
            <Route path="/stop-loss"          element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <StopLossPage />} />
            {/* Phase 4 — Member Profile */}
            <Route path="/member"             element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <MemberProfilePage />} />
            <Route path="/member/:patientId"      element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <MemberProfilePage />} />
            <Route path="/pricing-transparency"   element={!isOktaAuthenticated ? <OktaLogin onLogin={handleOktaLogin} /> : <PricingTransparencyPage />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    </div>
  );
}

function AppOkta() {
  return (
    <ThemeProvider>
      <MuiThemeWrapper>
        <Router>
          <AppContent />
        </Router>
      </MuiThemeWrapper>
    </ThemeProvider>
  );
}

export default AppOkta;
